import { useEffect, useState } from "react";

import { FilterMatchMode } from "primereact/api";
import { DataTable } from "primereact/datatable";
import { Column } from "primereact/column";
import { Tag } from "primereact/tag";
import { Button } from "primereact/button";
import { InputText } from "primereact/inputtext";
import { IconField } from "primereact/iconfield";
import { InputIcon } from "primereact/inputicon";
import { Skeleton } from "primereact/skeleton";

import { getUsersStatus } from "../../services/wisdomServices/userService";

function ManagerDashUserStatusTable({
  managerId,
  selectedProjectId = 0,
}) {
  const [usersStatus, setUsersStatus] = useState([]);
  const [filters, setFilters] = useState(null);
  const [globalFilterValue, setGlobalFilterValue] = useState("");
  const [loading, setLoading] = useState(true);

  const initFilters = () => {
    setFilters({
      global: {
        value: null,
        matchMode: FilterMatchMode.CONTAINS,
      },
    });

    setGlobalFilterValue("");
  };

  const loadUsersStatus = async () => {
    setLoading(true);

    try {
      const users = await getUsersStatus(selectedProjectId);

      const sortedUsers = [...users].sort((a, b) => {
        const aIsOnline = a.status?.toLowerCase() === "online";
        const bIsOnline = b.status?.toLowerCase() === "online";

        if (aIsOnline === bIsOnline) {
          return (a.fullName || "").localeCompare(b.fullName || "");
        }

        return aIsOnline ? -1 : 1;
      });

      setUsersStatus(sortedUsers);
    } catch (error) {
      console.error("Error loading users status:", error);
      setUsersStatus([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    initFilters();
  }, []);

  useEffect(() => {
    if (!managerId) {
      setUsersStatus([]);
      setLoading(false);
      return;
    }

    loadUsersStatus();
  }, [managerId, selectedProjectId]);

  const getSeverity = (userStatus) => {
    switch (userStatus.status?.toLowerCase()) {
      case "online":
        return "success";

      case "offline":
        return "danger";

      default:
        return null;
    }
  };

  const statusBodyTemplate = (userStatus) => {
    return (
      <Tag
        value={userStatus.status}
        severity={getSeverity(userStatus)}
      />
    );
  };

  const onGlobalFilterChange = (event) => {
    const value = event.target.value;

    setFilters((currentFilters) => ({
      ...currentFilters,
      global: {
        value,
        matchMode: FilterMatchMode.CONTAINS,
      },
    }));

    setGlobalFilterValue(value);
  };

  const header = (
    <div className="users-table-header">
      <h5 className="manager-dash-table-titles">
        Users: {usersStatus.length}
      </h5>

      <div className="table-filters">
        <Button
          type="button"
          icon="pi pi-filter-slash"
          label="Clear"
          outlined
          onClick={initFilters}
        />

        <IconField iconPosition="left">
          <InputIcon className="pi pi-search" />

          <InputText
            value={globalFilterValue}
            onChange={onGlobalFilterChange}
            placeholder="Keyword Search"
            style={{ paddingLeft: "2.25rem" }}
          />
        </IconField>
      </div>
    </div>
  );

  if (loading && usersStatus.length === 0) {
    return (
      <Skeleton
        width="100%"
        height="18rem"
        borderRadius="12px"
      />
    );
  }

  return (
    <DataTable
      value={usersStatus}
      header={header}
      tableStyle={{ minWidth: "10rem" }}
      filters={filters}
      globalFilterFields={["fullName"]}
      emptyMessage="No users found."
      onFilter={(event) => setFilters(event.filters)}
      paginator
      rows={50}
      rowsPerPageOptions={[10, 25, 50, 100]}
      loading={loading}
    >
      <Column
        field="id"
        header="ID"
        style={{ minWidth: "4rem" }}
      />

      <Column
        field="fullName"
        header="Name"
        style={{ minWidth: "10rem" }}
      />

      <Column
        field="status"
        header="Status"
        body={statusBodyTemplate}
      />
    </DataTable>
  );
}

export default ManagerDashUserStatusTable;